import type { Command } from 'commander';

/**
 * Self-describing capability catalog for the CLI.
 *
 * Why this exists: coding agents drive the CLI and treat `--help` as the
 * capability list. Flat per-subcommand help hides options (e.g. `--attach`
 * lives on `comment add` / `tasks create`, not on `tasks --help`), so agents
 * conclude a feature is missing when it isn't (see HTPR-3781 / HTPR-3807).
 * This walks the whole commander tree and emits every command + option in one
 * call, so an agent enumerates the full surface instead of grepping --help.
 */

export interface CapabilityArgument {
  name: string;
  required: boolean;
  variadic: boolean;
}

export interface CapabilityOption {
  flags: string;
  description: string;
  required: boolean;
  default?: unknown;
}

export interface CapabilityCommand {
  name: string;
  aliases: string[];
  description: string;
  arguments: CapabilityArgument[];
  options: CapabilityOption[];
  commands: CapabilityCommand[];
}

/** Walk a commander Command into a plain, serialisable capability tree. */
export function buildCapabilities(cmd: Command): CapabilityCommand {
  return {
    name: cmd.name(),
    aliases: cmd.aliases(),
    description: cmd.description(),
    arguments: cmd.registeredArguments.map((a) => ({
      name: a.name(),
      required: a.required,
      variadic: a.variadic,
    })),
    options: cmd.options
      .filter((o) => !o.hidden)
      .map((o) => {
        const opt: CapabilityOption = {
          flags: o.flags,
          description: o.description ?? '',
          required: Boolean(o.mandatory),
        };
        // Skip empty-array defaults (repeatable collectors) — they're noise.
        const def = o.defaultValue;
        if (def !== undefined && !(Array.isArray(def) && def.length === 0)) {
          opt.default = def;
        }
        return opt;
      }),
    commands: cmd.commands.map(buildCapabilities),
  };
}

/** Print the capability catalog as JSON (compact when --json, else indented). */
export function dumpCapabilities(cli: Command, json?: boolean): void {
  const tree = buildCapabilities(cli);
  console.log(JSON.stringify(tree, null, json ? 0 : 2));
}
